/* eslint-disable @typescript-eslint/camelcase */
import React, { ReactChild, useLayoutEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, Link } from 'react-router-dom';
import { TabDefinition } from '@increasecard/typed-components/dist/components/ApplicationLayout/types';
import { Layout, useCountry, Country } from '@increasecard/increase-core';
import { Services } from '@card/commonlib';
import { ENABLE_INTERNAL_NAVIGATION } from '../../flags';
import { useBlockedCredentialsAlert } from '../../features/credentials/useBlockedCredentialsAlert';

type LayoutBaseProps = {
  children: ReactChild;
};

const MainContent = styled.div`
  display: flex;
  position: relative;
  background: #fbfbfb;
  min-height: calc(100vh - 112px);
`;

const LayoutRestyleHack = createGlobalStyle`
  #root > div > div:first-child > div {
    background: #236;

    > * {
      color: #bcd;
      background: transparent;
      font-size: 13px;
      border-bottom-color: #0d4;

      & + * {
        margin-left: 26px;
      }
    }
  }
`;

const BASE_URL = ENABLE_INTERNAL_NAVIGATION ? '' : process.env.REACT_APP_CARD_V1_URL;
const LinkComponent = ENABLE_INTERNAL_NAVIGATION ? Link : 'a';
const hrefKey = ENABLE_INTERNAL_NAVIGATION ? 'to' : 'href';

const LayoutBase: React.FC<LayoutBaseProps> = ({ children }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const country = useCountry() as Country;
  useBlockedCredentialsAlert();

  useLayoutEffect(() => {
    if (country !== null) {
      Services.setTenant(country);
    }
  }, [country]);

  const ContextNavContentArg: TabDefinition[] = [
    {
      as: LinkComponent,
      active: history.location.pathname === '/dashboard',
      [hrefKey]: `${BASE_URL}/dashboard`,
      name: t(`common:ContextNavigation_v1:calendar`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/clearings',
      [hrefKey]: `${BASE_URL}/clearings`,
      name: t(`common:ContextNavigation_v1:clearings`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/monthlyClearings',
      [hrefKey]: `${BASE_URL}/monthlyClearings`,
      name: t(`common:ContextNavigation_v1:monthlyClearings`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/operations',
      [hrefKey]: `${BASE_URL}/operations`,
      name: t(`common:ContextNavigation_v1:operations`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/promotions',
      [hrefKey]: `${BASE_URL}/promotions`,
      name: t(`common:ContextNavigation_v1:promotions`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/chargebacks',
      [hrefKey]: `${BASE_URL}/chargebacks`,
      name: t(`common:ContextNavigation_v1:chargebacks`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname.includes('/tax'),
      [hrefKey]: `${BASE_URL}/taxations`,
      name: t(`common:ContextNavigation_v1:taxes`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/reports',
      [hrefKey]: `${BASE_URL}/reports`,
      name: t(`common:ContextNavigation_v1:reports`)
    },
    {
      as: Link,
      active: history.location.pathname.includes('/settings'),
      to: '/settings',
      name: t(`common:ContextNavigation_v1:settings`)
    }
  ];

  const ContextNavContentOther: TabDefinition[] = [
    {
      as: LinkComponent,
      active: history.location.pathname === '/dashboard',
      [hrefKey]: `${BASE_URL}/dashboard`,
      name: t(`common:ContextNavigation_v1:calendar`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/clearings',
      [hrefKey]: `${BASE_URL}/clearings`,
      name: t(`common:ContextNavigation_v1:clearings`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/operations',
      [hrefKey]: `${BASE_URL}/operations`,
      name: t(`common:ContextNavigation_v1:operations`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/promotions',
      [hrefKey]: `${BASE_URL}/promotions`,
      name: t(`common:ContextNavigation_v1:promotions`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/chargebacks',
      [hrefKey]: `${BASE_URL}/chargebacks`,
      name: t(`common:ContextNavigation_v1:chargebacks`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname.includes('/tax'),
      [hrefKey]: `${BASE_URL}/taxations`,
      name: t(`common:ContextNavigation_v1:taxes`)
    },
    {
      as: LinkComponent,
      active: history.location.pathname === '/reports',
      [hrefKey]: `${BASE_URL}/reports`,
      name: t(`common:ContextNavigation_v1:reports`)
    },
    {
      as: Link,
      active: history.location.pathname.includes('/settings'),
      to: '/settings',
      name: t(`common:ContextNavigation_v1:settings`)
    }
  ];

  return (
    <>
      <LayoutRestyleHack />
      <Layout tabs={country === 'argentina' ? ContextNavContentArg : ContextNavContentOther}>
        <MainContent>{children}</MainContent>
      </Layout>
    </>
  );
};

export default LayoutBase;
